import { OrderInputModel, OrdersFormFieldOptionViewModel, OrdersFormFieldViewModel } from '@ail/orders-api'
import { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Arr } from '../../../helpers/Arr'
import { ObjectHelper } from '../../../helpers/ObjectHelper'
import { ContactFormComponent } from '../ContactForm/ContactFormComponent'
import { CheckboxSection } from '../Sections/CheckboxSection'
import { DragndropSection } from '../Sections/DragndropSection'
import { RadioSection } from '../Sections/RadioSection'

export interface IFormField extends OrdersFormFieldViewModel {
  index: number
  options: IFormFieldOption[]
  hidden?: boolean
}

export interface IFormFieldOption extends OrdersFormFieldOptionViewModel {
  label?: string
  hidden?: boolean
}

interface IFormBuilderProps {
  form: IFormField[] // Just an array of fields
  formToSubmit: OrderInputModel
  submit: (form: OrderInputModel, captchaToken: string) => void
  loading: boolean
}

export function FormBuilder(props: IFormBuilderProps) {
  const emptyFormToSubmit: OrderInputModel = {
    approvalProcessConfigId: '',
    data: {},
    formId: '',
  }

  const [renderForm, setRenderForm] = useState<IFormField[]>([])
  const [valid, setValid] = useState<boolean>(false)
  const [data, setData] = useState<{ [id: string]: string }>({})
  const [, setFiles] = useState<{ [name: string]: string }>({})
  const [formToSubmit, setFormToSubmit] = useState<OrderInputModel>(emptyFormToSubmit)

  const { i18n } = useTranslation()

  useEffect(() => {
    updateForm(data)
    setFormToSubmit(props.formToSubmit)
  }, [])

  useEffect(() => {
    setFormToSubmit(props.formToSubmit)
  }, [props.formToSubmit])

  useEffect(() => {
    validateForm()
  }, [renderForm])

  useEffect(() => {
    updateForm(data)
  }, [props.form, data])

  /**
   * Called when data is changed
   * @param name field name
   * @param value new value
   * @param valid field is valid
   */
  const changeData = (name: string, value: string) => {
    const newData = ObjectHelper.copy(data)

    newData[name] = value

    if (value == '') {
      delete newData[name]
    } else {
      newData[name] = value
    }
    setData(newData)
  }

  /**
   * Called when file list is changed
   * @param files new file list
   */
  const changeFiles = (newFiles: { [name: string]: string }) => {
    setFiles(newFiles)
    setData({ ...data, ...newFiles })
  }

  /**
   * Validates form, checks if contract form can be shown
   * @param fileCount
   * @param dataCount
   */
  const validateForm = () => {
    const visibleFormCount = renderForm.filter((f) => !f.hidden && (!data[f.name] || data[f.name] === '')).length
    setValid(visibleFormCount == 0)
  }

  /**
   * Submit current form
   * @param form
   */
  const submit = (form: { [field: string]: string }, captchaToken: string) => {
    const newFormToSubmit = { ...formToSubmit }

    newFormToSubmit.data = { ...data, ...form }

    props.submit(newFormToSubmit, captchaToken)
  }

  const prevSectionValue = (sectionIndex: number, renderForm: IFormField[]): string | undefined => {
    for (let x = sectionIndex - 1; x > 0; x--) {
      const section = renderForm.find((f) => f.index === x)

      if (!!section && !section.hidden) {
        return data[section.name]
      }
    }
  }

  const canShowUploadFiles = () => {
    if (data.typeOfWork === 'damage' && data.damageType) {
      if (data.damageType !== 'glass' && data.activitiesDamage) {
        return true
      } else if (data.damageType === 'glass' && data.activitiesGlass) {
        return true
      }
    }

    if (data.typeOfWork === 'rmt' && data.activities) {
      return true
    }
    return false
  }

  /**
   * Updates render form.
   * @param data array of field values
   */
  const updateForm = (data: { [id: string]: string }) => {
    const newRenderForm: IFormField[] = []
    props.form.map((field) => {
      if (field.index > 1 && !!field.visibleWhen) {
        if (prevSectionValue(field.index, newRenderForm)) {
          field.hidden = false
        } else {
          if (!field.hidden) {
            changeData(field.name, '')
          }

          field.hidden = true
        }

        // Hide options
        field.options?.map((option) => {
          if (option.visibleWhen) {
            Object.keys(option.visibleWhen).forEach((fieldName) => {
              if (
                !!option.visibleWhen &&
                !!option.visibleWhen[fieldName].find((f) => !!data[fieldName]?.split(',').find((d) => d === f))
              ) {
                option.hidden = false
              } else {
                option.hidden = true
              }
            })
          } else {
            option.hidden = false
          }

          if (option.hidden && data[field.name] === option.value) {
            if (data[field.name]) {
              changeData(field.name, '')
            }

            field.hidden = true
          }
        })

        // Hide sections
        Object.keys(field.visibleWhen).forEach((name) => {
          if (
            !field.hidden &&
            !!field.visibleWhen &&
            !!field.visibleWhen[name].find((f) => !!data[name]?.split(',').find((d) => d === f)) &&
            (!field.options?.length || !!field.options?.find((o) => !o.hidden))
          ) {
            if (field.name === 'offer' && !canShowUploadFiles()) {
              field.hidden = true
            } else {
              field.hidden = false
            }
          } else {
            if (data[field.name]) {
              changeData(field.name, '')
            }

            field.hidden = true
          }
        })
      } else {
        field.hidden = false
      }
      // TODO: add option filtering

      newRenderForm.push(field)
    })

    setRenderForm(newRenderForm)
  }

  const getRenderForm = () => {
    const renderSections: ReactElement[] = []
    const lang = i18n.language

    if (!!renderForm && Arr.any(renderForm)) {
      renderForm.map((field) => {
        let section: ReactElement | undefined = undefined
        let title = field.label

        if (field.tr) {
          title = field.tr[lang] ?? field.name
        } else {
          title = field.name
        }

        field.options?.map((option) => {
          if (!!option.tr && option.tr[lang]) {
            option.label = option.tr[lang]
          } else {
            option.label = option.name
          }
        })

        switch (field.type) {
          case 'radio':
            if (field.options) {
              section = (
                <RadioSection
                  key={renderSections.length}
                  className="firstChoiceList"
                  name={field.name}
                  title={title}
                  hidden={field.hidden}
                  fields={field.options}
                  disabled={props.loading}
                  handleChange={(name, value) => changeData(name, value)}
                />
              )
            }
            break
          case 'multiselect':
            if (field.options) {
              section = (
                <CheckboxSection
                  key={renderSections.length}
                  className="firstChoiceList"
                  name={field.name}
                  title={title}
                  hidden={field.hidden}
                  fields={field.options}
                  disabled={props.loading}
                  handleChange={(name, value) => changeData(name, value)}
                />
              )
            }
            break
        }

        if (section) {
          renderSections.push(section)
        }
      })

      // Files upload section
      let section: ReactElement = <></>
      const uploadFields = renderForm.filter((f) => f.type === 'upload' && !f.hidden)

      const titles = uploadFields.map((field) => {
        return field.tr ? field.tr[lang] : (field.label ?? '')
      })

      const names = uploadFields.map((field) => {
        return field.name ?? 'offer'
      })

      const hidden = !Arr.any(uploadFields.filter((f) => !f.hidden))

      if (hidden) {
        renderForm
          .filter((f) => f.type === 'upload')
          .map((field) => {
            if (data[field.name]) {
              changeData(field.name, '')
            }
          })
      }

      section = (
        <DragndropSection
          key={renderSections.length}
          names={names}
          title="Upload your files"
          hidden={hidden}
          descriptions={titles}
          disabled={props.loading}
          handleChange={(files: { [name: string]: string }) => changeFiles(files)}
        />
      )

      renderSections.push(section)
    }

    return renderSections
  }

  return (
    <>
      {getRenderForm()}

      <ContactFormComponent
        loading={props.loading}
        handleSubmit={(form, captchaToken) => submit(form, captchaToken)}
        hidden={!valid}
      />
    </>
  )
}
